/**
 * Keyboard shortcut registry.
 *
 * Every shortcut bound anywhere in the app lists itself here, in
 * one place, so the help overlay can render an authoritative
 * legend. Adding a new shortcut without registering it here is a
 * lint-adjacent bug — reviewers should flag it.
 *
 * The registry itself does NOT bind keys — binding lives at the
 * call site (app-shell, command palette, etc.). The registry is
 * documentation and discovery.
 */

export interface ShortcutDef {
  /** The key sequence, macOS-formatted. */
  keys: string;
  /** Non-mac fallback (e.g. "Ctrl + K"). Omit if identical. */
  nonMacKeys?: string;
  /** Which group the shortcut lives in. */
  group: "Global" | "Navigation" | "Forms";
  /** Human label for the help overlay. */
  label: string;
  /** Where the binding lives — useful for grep-based audits. */
  source: string;
}

export const SHORTCUT_REGISTRY: readonly ShortcutDef[] = [
  {
    keys: "⌘ K",
    nonMacKeys: "Ctrl + K",
    group: "Global",
    label: "Open command palette",
    source: "features/command-palette/command-palette.tsx",
  },
  {
    keys: "?",
    group: "Global",
    label: "Keyboard shortcuts help",
    source: "features/shortcuts/shortcuts-overlay.tsx",
  },
  {
    keys: "/",
    group: "Navigation",
    label: "Find a setting (Authentication)",
    source: "features/signin-settings/components/settings-finder.tsx",
  },
  {
    keys: "Esc",
    group: "Global",
    label: "Close the command palette or any open dialog",
    source: "features/command-palette/command-palette.tsx",
  },
  {
    keys: "⌘ ↵",
    nonMacKeys: "Ctrl + ↵",
    group: "Forms",
    label: "Submit the current form",
    source: "default browser behavior",
  },
  {
    keys: "⌘ Z",
    nonMacKeys: "Ctrl + Z",
    group: "Forms",
    label: "Undo in input fields",
    source: "default browser behavior",
  },
];
